import {
  ORDER_SET_ENTITY_ID_SYSTEM,
  SCHEDULE_ENTITY_ID_SYSTEM,
  VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
  VISIT_EXECUTION_MEMBER_ID_SYSTEM,
  VISIT_KIND_SYSTEM,
} from "@repo/api";
import { ACT_CODE_SYSTEM } from "@repo/fhir-systems";
import { ENCOUNTER_CLASS_URL } from "@repo/fhir-systems/extensions";
import { getDataStoreClient } from "../core/provider";
import type { AppointmentRow, SlotRow } from "../fhir/types";
import { optimisticPatientReference } from "./_patient-reference";

const APPOINTMENT_COLLECTION = "appointment";
const appointments = () => getDataStoreClient().getCollection<AppointmentRow, string>(APPOINTMENT_COLLECTION);

/**
 * Insert the shared optimistic Appointment consequence inside the calling
 * action transaction. Naming the set by identity in `supportingInformation` is
 * what flips its orders to Scheduled before the server answers.
 */
export function insertOptimisticAppointment(input: {
  id: string;
  patientId: string;
  candidate: {
    start: string;
    end: string;
    placements?: ReadonlyArray<{ scheduleId: string; start: string; end: string }>;
  };
  modality?: "virtual" | "in-person";
  kind?: "consultation" | "curated-session" | "standalone";
  orderSetId?: string;
}): void {
  const visitId = input.id;
  const client = getDataStoreClient();
  const slots: SlotRow[] = (input.candidate.placements ?? []).map((placement, index) => ({
    resourceType: "Slot",
    status: "busy",
    identifier: [
      { system: VISIT_ENTITY_ID_SYSTEM, value: visitId },
      { system: VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM, value: `assignment-${index + 1}` },
    ],
    slotKey: `${visitId}/assignment-${index + 1}`,
    schedule: { identifier: { system: SCHEDULE_ENTITY_ID_SYSTEM, value: placement.scheduleId } },
    scheduleJoinKey: placement.scheduleId,
    start: placement.start,
    end: placement.end,
  }));
  for (const name of ["slot", "booked-slot"] as const) {
    for (const slot of slots) client.getCollection(name).insert(slot);
  }
  const encounterClass = { system: ACT_CODE_SYSTEM, code: input.modality === "virtual" ? "VR" : "AMB" };
  client.getCollection("encounter").insert({
    resourceType: "Encounter",
    status: "planned",
    class: encounterClass,
    identifier: [{ system: VISIT_EXECUTION_MEMBER_ID_SYSTEM, value: "execution-1" }],
    appointment: [{ identifier: { system: VISIT_ENTITY_ID_SYSTEM, value: visitId } }],
    subject: optimisticPatientReference(input.patientId),
    visitId,
    visitJoinKey: visitId,
  });
  appointments().insert({
    resourceType: "Appointment",
    identifier: [{ system: VISIT_ENTITY_ID_SYSTEM, value: visitId }],
    domainId: visitId,
    patientId: input.patientId,
    ...(input.orderSetId ? { orderSetId: input.orderSetId } : {}),
    orderSetJoinKey: input.orderSetId ?? `_noorderset_${visitId}`,
    firstSlotJoinKey: slots[0]?.slotKey ?? `_noslot_${visitId}`,
    slotKeys: slots.map((slot) => slot.slotKey),
    slot: slots.map((slot) => ({ identifier: slot.identifier?.[1] })),
    appointmentType: { coding: [{ system: VISIT_KIND_SYSTEM, code: input.kind ?? "consultation" }] },
    extension: [{ url: ENCOUNTER_CLASS_URL, valueCoding: encounterClass }],
    status: "booked",
    start: input.candidate.start,
    end: input.candidate.end,
    ...(input.orderSetId
      ? { supportingInformation: [{ identifier: { system: ORDER_SET_ENTITY_ID_SYSTEM, value: input.orderSetId } }] }
      : {}),
    participant: [{ actor: optimisticPatientReference(input.patientId), status: "accepted" }],
  });
}
